import React from 'react'

/*
 * FloatingContactButtons
 *
 * Bottom-right pair of always-on contact shortcuts. Both buttons
 * funnel through the page-level container's callbacks so we can run
 * the visitor through the ContactTeamModal before handing them off —
 * that lets us capture a callback number in case the conversation
 * drops, for both voice and WhatsApp paths.
 *
 * Positioning uses a CSS variable (`--ct-floating-bottom`) so pages
 * that already have a bottom bar (e.g. the mentor landing page's
 * "Request a session" strip) can lift the buttons higher without
 * touching JS.
 */

export default function FloatingContactButtons({ onPhoneClick, onWhatsappClick }) {
  return (
    <div className="ct-floating" role="group" aria-label="Quick contact">
      <button
        type="button"
        className="ct-floating-btn ct-floating-wa"
        onClick={onWhatsappClick}
        aria-label="Chat with AUMHUM on WhatsApp"
      >
        <WhatsappGlyph />
      </button>
      <button
        type="button"
        className="ct-floating-btn ct-floating-phone"
        onClick={onPhoneClick}
        aria-label="Call AUMHUM"
      >
        <PhoneGlyph />
      </button>
    </div>
  )
}

// Inline SVG glyphs — deliberately local so this file stays a single
// drop-in component for any page that wants the floating contact pair.

// Canonical WhatsApp brand mark: speech bubble with the inset
// phone-receiver silhouette knocked out. Rendered as two-tone by
// relying on an inner `fillRule="evenodd"` path so the bubble stays
// one colour (currentColor) while the receiver reads as background.
function WhatsappGlyph() {
  return (
    <svg
      viewBox="0 0 32 32"
      width="24"
      height="24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.576 5.363C23.75 2.536 19.993 0.979 16.015 0.977 7.802 0.977 1.122 7.655 1.119 15.868c-0.001 2.625 0.685 5.188 1.99 7.448L0.994 30.956l7.82-2.05c2.176 1.187 4.623 1.812 7.114 1.813h0.006c8.212 0 14.893-6.68 14.896-14.892 0.002-3.976-1.548-7.714-4.254-10.464zM16.014 28.207h-0.005c-2.22-0.001-4.399-0.597-6.301-1.724l-0.452-0.268-4.64 1.218 1.238-4.525-0.294-0.468c-1.237-1.967-1.891-4.242-1.89-6.571 0.003-6.822 5.553-12.371 12.381-12.371 3.308 0.001 6.414 1.291 8.752 3.632 2.338 2.342 3.625 5.451 3.624 8.762-0.003 6.823-5.553 12.374-12.373 12.374zM22.792 19.218c-0.372-0.187-2.2-1.086-2.541-1.21-0.341-0.124-0.589-0.187-0.838 0.187-0.248 0.372-0.961 1.21-1.178 1.458-0.217 0.248-0.434 0.279-0.806 0.093-0.372-0.187-1.57-0.578-2.99-1.844-1.105-0.986-1.852-2.204-2.069-2.576-0.217-0.372-0.023-0.573 0.163-0.759 0.167-0.167 0.372-0.434 0.558-0.651 0.186-0.217 0.248-0.372 0.372-0.62 0.124-0.248 0.062-0.465-0.031-0.651-0.093-0.187-0.838-2.019-1.148-2.764-0.302-0.726-0.609-0.628-0.838-0.64-0.217-0.011-0.465-0.013-0.713-0.013-0.248 0-0.651 0.093-0.992 0.465-0.341 0.372-1.302 1.271-1.302 3.103 0 1.832 1.333 3.601 1.519 3.849 0.187 0.248 2.623 4.005 6.354 5.616 0.888 0.384 1.581 0.613 2.122 0.784 0.891 0.283 1.702 0.243 2.343 0.147 0.715-0.107 2.2-0.9 2.51-1.768 0.31-0.869 0.31-1.613 0.217-1.768-0.093-0.155-0.341-0.248-0.713-0.434z"
      />
    </svg>
  )
}

function PhoneGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
      />
    </svg>
  )
}
